import { FC, memo, useCallback, useMemo, useState } from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import { useDestructiveActionUndo } from "@/app/providers/DestructiveActionUndoContext";
import { ProcedureNodeStepComment } from "@/features/procedures/model/procedure/ProcedureNodeStepComment";
import { ProcedureNodeStep } from "@/features/procedures/model/types/checklist";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { FeedbackCard } from "@/shared/ui";
import { Box, Chip, Stack, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { useDeleteProcedureComment } from "@/service/ProcedureMutations";

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(relativeTime);

const clampStyles = (lines: number) => ({
	display: "-webkit-box",
	WebkitLineClamp: lines,
	WebkitBoxOrient: "vertical" as const,
	overflow: "hidden",
	// Soft fade at the bottom for a modern, polished clamp
	maskImage: "linear-gradient(to bottom, black 70%, transparent 100%)",
});

interface StepCommentProps {
	comment: ProcedureNodeStepComment;
	step: ProcedureNodeStep;
	activeSchemeId: number | undefined;
	isOnlyComments?: boolean;
}

export const StepComment: FC<StepCommentProps> = memo((props) => {
	const { comment, step, activeSchemeId, isOnlyComments } = props;

	const [isExpanded, setIsExpanded] = useState(false);
	const [hidden, setHidden] = useState(false);
	const { timezone } = useTime();
	const deleteComment = useDeleteProcedureComment();
	const { showDeferredUndo } = useDestructiveActionUndo();

	const handleDelete = useCallback(
		async (id: number) => {
			const request = {
				commentId: id,
				checklistId: activeSchemeId!,
				stepId: step.Id,
			};
			try {
				const result = await AlertRequest.delete();
				if (!result.isConfirmed) return;

				setHidden(true);
				showDeferredUndo({
					message: "Comment deleted - Undo (30s)",
					onUndo: () => setHidden(false),
					onCommit: async () => {
						await deleteComment.mutateAsync(request);
					},
				});
			} catch (error) {
				console.error("Failed to delete comment", error);
				AlertRequest.failed();
			}
		},
		[activeSchemeId, step.Id, deleteComment, showDeferredUndo],
	);

	// A bit more space when viewing "only comments"
	const collapsedLines = isOnlyComments ? 6 : 3;

	const createdAt = useMemo(
		() => dayjs.utc(comment.Date).tz(timezone),
		[comment.Date, timezone],
	);

	const exact = useMemo(
		() => createdAt.format("DD/MM/YY HH:mm:ss"),
		[createdAt],
	);

	const relative = useMemo(() => createdAt.fromNow(), [createdAt]);

	if (hidden) {
		return null;
	}

	return (
		<FeedbackCard
			accent="info"
			comment={comment}
			title={<Translate String="Procedure.Checklist.Step.Comment.Comment" />}
			isExpanded={isExpanded}
			isDeletable
			onDelete={() => {
				void handleDelete(comment.Id);
			}}
			toggleExpand={() => setIsExpanded((v) => !v)}
			chip={
				<Tooltip title={exact} arrow>
					<Chip
						size="small"
						label={relative}
						variant="outlined"
						sx={{
							height: 24,
							borderRadius: 1.5,
							"& .MuiChip-label": { px: 0.75 },
							"& .MuiChip-icon": { color: "text.secondary" },
						}}
					/>
				</Tooltip>
			}
		>
			<Stack spacing={0.5} sx={{ width: "100%" }}>
				<Box sx={isExpanded ? undefined : clampStyles(collapsedLines)}>
					<SATOSContentRender content={comment.Value} />
				</Box>
			</Stack>
		</FeedbackCard>
	);
});
